/**
 * The host half of the MCP-Apps widget protocol, as Claude hosts prophecy-mcp's widgets: JSON-RPC
 * over postMessage with the widget's bridge (prophecy-mcp/widgets-src/shared/host/bridge.ts).
 * Pure — the iframe component owns the window plumbing.
 */

export type WidgetDisplayMode = 'inline' | 'fullscreen';

export type WidgetToolResult = { structuredContent: unknown; _meta?: Record<string, unknown> };

export type HomeChatWidget = { name?: string | null; structuredContent: unknown; _meta?: Record<string, unknown> };

/**
 * The `ui://` resource a widget tool result points at (prophecy-mcp widgetToolMeta). A result
 * carrying no widget name and no uri is plain data — the sandbox attaches every structuredContent.
 */
export function resourceUriOf(widget: HomeChatWidget): string | undefined {
  const meta = widget._meta ?? {};
  const fromMeta = meta['ui/resourceUri'] ?? (meta.ui as { resourceUri?: unknown } | undefined)?.resourceUri;
  if (typeof fromMeta === 'string') return fromMeta;
  return widget.name ? `ui://prophecy/${widget.name}.html` : undefined;
}

/**
 * The router path for a link into this same deployment under the app's base path — what a widget's
 * "Open in Prophecy" points at — or undefined for anything that belongs in a new tab.
 */
export function inAppPath(url: string, { basePath, origins }: { basePath: string; origins: string[] }) {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return undefined;
  }
  if (!origins.includes(parsed.origin)) return undefined;
  if (parsed.pathname !== basePath && !parsed.pathname.startsWith(`${basePath}/`)) return undefined;
  return `${parsed.pathname.slice(basePath.length) || '/'}${parsed.search}${parsed.hash}`;
}

type WidgetHostDeps = {
  post: (message: unknown) => void;
  toolResult: () => WidgetToolResult;
  authorization: () => string | undefined;
  callTool: (name: string, args: Record<string, unknown>) => Promise<unknown>;
  sendFollowUp: (text: string) => Promise<unknown>;
  openLink: (url: string) => void;
  download: (fileName: string, mimeType: string, bytes: string) => void;
  setDisplayMode: (mode: WidgetDisplayMode) => void;
  setHeight: (height: number) => void;
  onInitialized?: () => void;
};

type RpcMessage = { jsonrpc?: unknown; id?: number | string; method?: unknown; params?: unknown };
type DownloadContent = { type?: string; resource?: { uri?: string; mimeType?: string; blob?: string } };

const THEME = 'light';

/** An `Error`'s own message, or `fallback` for anything thrown that isn't one. */
export function formatError(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export function createWidgetHost(deps: WidgetHostDeps) {
  let displayMode: WidgetDisplayMode = 'inline';

  const reply = (id: number | string, result: unknown) => deps.post({ jsonrpc: '2.0', id, result });
  const fail = (id: number | string, code: number, message: string) =>
    deps.post({ jsonrpc: '2.0', id, error: { code, message } });

  const toolResultWithAuth = (): WidgetToolResult => {
    const result = deps.toolResult();
    const authorization = deps.authorization();
    return authorization ? { ...result, _meta: { ...result._meta, authorization } } : result;
  };

  // `composerOverlay: false`: our composer sits outside the widget, so it keeps no band for one.
  const hostContext = () => ({ theme: THEME, displayMode, composerOverlay: false });

  async function handle(data: unknown): Promise<void> {
    const message = data as RpcMessage | null;
    if (!message || message.jsonrpc !== '2.0' || typeof message.method !== 'string') return;
    const { id, method } = message;
    const params = (message.params ?? {}) as Record<string, unknown>;

    switch (method) {
      case 'ui/notifications/size-changed':
        if (typeof params.height === 'number') deps.setHeight(params.height);
        return;
      case 'ui/notifications/initialized':
        deps.onInitialized?.();
        return;
    }
    if (id === undefined) return;

    switch (method) {
      case 'ui/initialize':
        return reply(id, {
          hostCapabilities: { downloadFile: {} },
          hostContext: hostContext(),
          toolResult: toolResultWithAuth()
        });
      case 'tools/call':
        try {
          return reply(
            id,
            await deps.callTool(String(params.name ?? ''), (params.arguments ?? {}) as Record<string, unknown>)
          );
        } catch (error) {
          return fail(id, -32603, formatError(error, 'Request failed'));
        }
      case 'ui/message': {
        const content = Array.isArray(params.content)
          ? (params.content as Array<{ type?: string; text?: string }>)
          : [];
        const text = content
          .filter((part) => part.type === 'text' && part.text)
          .map((part) => part.text)
          .join('');
        if (text) await deps.sendFollowUp(text);
        return reply(id, {});
      }
      case 'ui/open-link': {
        const url = typeof params.url === 'string' ? params.url : '';
        if (!/^https?:\/\//i.test(url)) return reply(id, { isError: true });
        deps.openLink(url);
        return reply(id, {});
      }
      case 'ui/download-file': {
        const content = (Array.isArray(params.contents) ? params.contents : [])[0] as DownloadContent | undefined;
        const resource = content?.type === 'resource' ? content.resource : undefined;
        if (!resource?.blob || !resource.uri) return reply(id, { isError: true });
        try {
          deps.download(resource.uri.split('/').pop() || 'download', resource.mimeType ?? '', atob(resource.blob));
          return reply(id, {});
        } catch {
          return reply(id, { isError: true });
        }
      }
      case 'ui/request-display-mode':
        displayMode = params.mode === 'fullscreen' ? 'fullscreen' : 'inline';
        deps.setDisplayMode(displayMode);
        return reply(id, { mode: displayMode });
      default:
        return fail(id, -32601, `Method not found: ${method}`);
    }
  }

  return {
    handle,
    deliverToolResult: () =>
      deps.post({ jsonrpc: '2.0', method: 'ui/notifications/tool-result', params: toolResultWithAuth() }),
    notifyDisplayMode: (mode: WidgetDisplayMode) => {
      displayMode = mode;
      deps.post({
        jsonrpc: '2.0',
        method: 'ui/notifications/host-context-changed',
        params: { hostContext: hostContext() }
      });
    }
  };
}
